import { useEffect, useState } from 'react';
import { PriceWebSocketManager } from '../services/priceWebSocketManager';
import type { HeaderPrices } from '../services/priceWebSocketManager';

// Match existing UI_UPDATE_INTERVAL from other hooks (75ms)
const UI_UPDATE_INTERVAL = 75;

// Singleton pattern: Share one manager instance across all components
// This prevents reconnection churn during React Strict Mode double-mounting in dev
let managerInstance: PriceWebSocketManager | null = null;
let connectionRefCount = 0;

/**
 * usePriceUpdates - Hook for real-time price updates in header statistics
 *
 * CRITICAL ARCHITECTURE:
 * - Singleton manager persists across component remounts
 * - WebSocket onmessage → Update manager internal state (NO React setState)
 * - Ticker interval (75ms) → Read from manager → Update React state
 * - This batches renders and prevents performance issues
 * - Only disconnects when ALL components using this hook unmount
 *
 * @returns HeaderPrices | null - Current prices from all exchanges, or null if loading
 */
export const usePriceUpdates = (): HeaderPrices | null => {
  // Initialize with current prices if manager already exists (for instant display on remount)
  const [prices, setPrices] = useState<HeaderPrices | null>(() =>
    managerInstance?.getPrices() ?? null
  );

  useEffect(() => {
    // Create singleton instance only once
    if (!managerInstance) {
      console.log('[Hook usePriceUpdates] Creating singleton manager instance...');
      managerInstance = new PriceWebSocketManager();

      // Connect to all exchanges (only once)
      managerInstance.connectBinance();
      managerInstance.connectUpbit();
      managerInstance.connectBithumb();

      // Start polling Naver Finance for official USD/KRW rate
      managerInstance.startNaverPolling();

      console.log('[Hook usePriceUpdates] All exchanges connected');
    }

    // Track how many components are using this hook
    connectionRefCount++;
    console.log(`[Hook usePriceUpdates] Component mounted (refCount: ${connectionRefCount})`);

    // Ticker pattern: Read from manager and update UI every 75ms
    // This batches React renders and prevents performance issues
    const ticker = setInterval(() => {
      if (managerInstance) {
        const currentPrices = managerInstance.getPrices();
        setPrices(currentPrices);
      }
    }, UI_UPDATE_INTERVAL);

    console.log('[Hook usePriceUpdates] Price ticker started (interval:', UI_UPDATE_INTERVAL, 'ms)');

    // Cleanup on unmount
    return () => {
      clearInterval(ticker);
      connectionRefCount--;

      console.log(`[Hook usePriceUpdates] Component unmounted (refCount: ${connectionRefCount})`);

      // Only disconnect when NO components are using the manager
      if (connectionRefCount === 0 && managerInstance) {
        console.log('[Hook usePriceUpdates] Last component unmounted - disconnecting all exchanges...');
        managerInstance.disconnect();
        managerInstance = null;
      }
    };
  }, []); // Empty deps - setup once per component mount

  return prices;
};
